import { Controller } from "@hotwired/stimulus"

// Obsługuje granty uprawnień na formatce roli:
//  - po zaznaczeniu uprawnienia A zaznacza wszystkie nadawane (przechodnie domknięcie data-grants)
//  - po odznaczeniu uprawnienia A odznacza wszystko, co A nadawało (kaskada w drugą stronę:
//    np. odznaczenie "crm_clients/own_access" odznacza "crm_clients/own_edit")
//  - nadawane przez aktualne zaznaczenia są wizualnie oznaczone (ring + niebieski tekst)
// Źródłem prawdy jest Account::Role::PRIVILEGE_GRANTS; do widoku trafia przez data-grants.
export default class extends Controller {
  static targets = ["checkbox"]
  static values = {
    grantsTitle: String,
  }

  connect() {
    this.byValue = {}
    // Granty cache'owane przy connect — data-grants to JSON, parsowany raz per checkbox
    // zamiast przy każdym recompute (każda zmiana w formatce wołałaby JSON.parse N razy).
    this.grantsFor = {}
    // Mapa odwrócona: value → lista checkboxów, które nadają tę value.
    // Używana do kaskadowego odznaczania (gdy odznaczam X, lecą wszystkie zależne).
    this.dependents = {}
    // Mapa labelów per checkbox — żeby nie wołać querySelector przy każdej zmianie
    // i ominąć problem ze znakami specjalnymi w id (np. "/", "."), które wymagałyby CSS.escape.
    this.labelByValue = {}

    this.checkboxTargets.forEach((cb) => {
      this.byValue[cb.value] = cb
      const grants = this.parseGrants(cb)
      this.grantsFor[cb.value] = grants
      grants.forEach((g) => {
        if (!this.dependents[g]) this.dependents[g] = []
        this.dependents[g].push(cb.value)
      })
      const label = this.element.querySelector(`label[for="${CSS.escape(cb.id)}"]`)
      if (label) this.labelByValue[cb.value] = label
    })

    this.recompute()
  }

  handleChange(event) {
    const cb = event.target
    if (cb.checked) {
      this.toggleGroupMembers(cb, true)
    } else {
      this.toggleGroupMembers(cb, false)
      this.cascadeUncheck(cb.value)
    }
    this.recompute()
  }

  // Gdy ręcznie klikamy mastera grupy (np. "account"), zaznacza/odznacza wszystkie
  // sub-uprawnienia z tej grupy. Symetryczne — bez tego subs z `group:` (bez grantu do mastera)
  // nie znalazłyby się w `dependents` i nie odznaczyłyby się przez cascadeUncheck.
  toggleGroupMembers(cb, checked) {
    const raw = cb.dataset.groupMembers
    if (!raw) return
    let members
    try {
      members = JSON.parse(raw)
    } catch {
      return
    }
    if (!Array.isArray(members)) return
    members.forEach((m) => {
      const memberCb = this.byValue[m]
      if (!memberCb || memberCb.disabled) return
      if (memberCb.checked === checked) return
      memberCb.checked = checked
      // Przy odznaczaniu zejdź też po dependents danego suba (np. account/utils
      // ma sub login_on_other_user, którego trzeba odhaczyć).
      if (!checked) this.cascadeUncheck(m)
    })
  }

  // Odznacza przechodnie wszystkie checkboxy, które (bezpośrednio lub pośrednio) nadają
  // właśnie odznaczone uprawnienie. Bez tego user musiałby ręcznie odznaczyć kilka dzieci
  // przed odznaczeniem rodzica (np. crm_clients/own_edit przed crm_clients/own_access).
  cascadeUncheck(value) {
    const queue = [value]
    const visited = new Set()
    while (queue.length > 0) {
      const v = queue.shift()
      if (visited.has(v)) continue
      visited.add(v)

      const deps = this.dependents[v] || []
      deps.forEach((d) => {
        const cb = this.byValue[d]
        if (cb && cb.checked && !cb.disabled) {
          cb.checked = false
          queue.push(d)
        }
      })
    }
  }

  // Przelicza stan grantów: zbiór wartości nadawanych przez zaznaczone źródła,
  // domyka je w "checked" i nakłada wizualne oznaczenie. Wartości, które wypadły z domknięcia,
  // tracą oznaczenie.
  recompute() {
    const grantedSet = this.collectGrantedFromChecked()

    this.checkboxTargets.forEach((cb) => {
      if (grantedSet.has(cb.value)) {
        if (!cb.checked) cb.checked = true
        this.markAsGranted(cb)
      } else {
        this.unmarkAsGranted(cb)
      }
    })
  }

  collectGrantedFromChecked() {
    const granted = new Set()
    const queue = []

    this.checkboxTargets.forEach((cb) => {
      if (cb.checked) {
        (this.grantsFor[cb.value] || []).forEach((v) => queue.push(v))
      }
    })

    while (queue.length > 0) {
      const value = queue.shift()
      if (granted.has(value)) continue
      granted.add(value)
      ;(this.grantsFor[value] || []).forEach((v) => queue.push(v))
    }

    return granted
  }

  markAsGranted(cb) {
    if (cb.dataset.grantedState === "auto") return
    cb.dataset.grantedState = "auto"
    cb.classList.add("ring-2", "ring-blue-300", "rounded")
    cb.title = this.grantsTitleValue || ""
    const label = this.labelByValue[cb.value]
    if (label) label.classList.add("text-blue-700")
  }

  unmarkAsGranted(cb) {
    if (cb.dataset.grantedState !== "auto") return
    delete cb.dataset.grantedState
    cb.classList.remove("ring-2", "ring-blue-300", "rounded")
    cb.title = ""
    const label = this.labelByValue[cb.value]
    if (label) label.classList.remove("text-blue-700")
  }

  parseGrants(cb) {
    const raw = cb.dataset.grants
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
}
